import React, { FC, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Navigate, matchRoutes, useLocation } from 'react-router-dom';
import { Card, CardBody, CardTitle, Container } from 'reactstrap';

import { isNumber } from 'lodash-es';

import GolfClubAvailability from '../../components/GolfClub/GolfClubAvailability';
import GolfClubCarousel from '../../components/GolfClub/GolfClubCarousel';
import GolfClubInfo from '../../components/GolfClub/GolfClubInfo';
import GolfCourses from '../../components/GolfCourses/GolfCourses';
import PageSpinner from '../../components/PageSpinner';
import Helmet from '../../components/common/Helmet';
import { HEADER_ELEMENT_ID } from '../../components/router/consts';
import Map from '../../containers/Map';
import { calculateDistance, sortDistance } from '../../helpers/distance';
import { formatISODate } from '../../helpers/formatISODate';
import { sortPromotedGolfCourses } from '../../helpers/sortPromotedGolfCourses';
import { useAccommodationFilter } from '../../hooks/useAccommodationFilter';
import { useDatesFilter } from '../../hooks/useDatesFilter';
import { calculateHidingHeaderOffset, getCurrentOffset } from '../../hooks/useHidingHeader';
import usePopularGolfCourses from '../../hooks/usePopularGolfCourses';
import usePrevious from '../../hooks/usePrevious';
import useTranslate from '../../hooks/useTranslate';
import useGolfCourses from '../../services/apis/golfCourses/api';
import getGolfClubInfo from '../../services/apis/golfCourses/getGolfClubInfo';
import { GolfClubPromotion, MappedGolfCourseType } from '../../services/apis/golfCourses/types';
import useStore from '../../store/useStore';

const VISIBLE_COURSES_AMOUNT_DEFAULT = 31;
const SCROLL_TOP_OFFSET = -20;
const MAX_DISTANCE = 100;

const filterAndSortCourses = (
  golfCourses: MappedGolfCourseType[],
  selectedGolfCourse: MappedGolfCourseType,
  isGolfhaftetRequest: boolean
) => {
  if (!selectedGolfCourse) return [];

  let filteredCourses: MappedGolfCourseType[] = [];
  if (isGolfhaftetRequest) {
    filteredCourses = golfCourses.filter(
      golfCourse => golfCourse.id !== selectedGolfCourse.id && golfCourse.golfCourse.golfhaftetId
    );
  } else {
    filteredCourses = golfCourses.filter(golfCourse => golfCourse.id !== selectedGolfCourse.id);
  }

  return [
    ...filteredCourses
      .map(golfCourse => ({
        ...golfCourse,
        distance: calculateDistance(selectedGolfCourse, golfCourse),
      }))
      .sort(sortDistance),
  ];
};

const BookingPage: FC = () => {
  const [sortedCourses, setSortedCourses] = useState<MappedGolfCourseType[]>([]);
  const [mapCourses, setMapCourses] = useState<MappedGolfCourseType[]>([]);
  const availabilityRef = useRef<HTMLDivElement>(null);

  const location = useLocation();
  const { translate, activeLanguage } = useTranslate();
  const golfCoursesQuery = useGolfCourses();
  const [isAccommodationFilter] = useAccommodationFilter();
  const golfCourses = useMemo(
    () =>
      golfCoursesQuery.data?.filter(golfCourse =>
        isAccommodationFilter ? !!getGolfClubInfo(golfCourse)?.accommodationId : true
      ),
    [golfCoursesQuery.data, isAccommodationFilter]
  );
  const popularGolfCourses = usePopularGolfCourses();
  const selectedGolfCourse = useMemo(() => {
    // If golf courses isn't downloaded yet the customer will be
    // redirected to first page since no match is found. Make sure we wait
    // until we have golf course data
    if (!golfCourses) return;

    const [matchCourse] =
      matchRoutes([{ path: '/:locale/booking/:clubId/:courseId' }], location) || [];

    if (matchCourse) {
      let { clubId: clubName, courseId: courseName } = matchCourse.params;
      const searchFor = `${clubName} - ${courseName}`;

      return golfCourses?.find(g => g.name === searchFor);
    }
  }, [golfCourses, location]);
  const isGolfhaftetRequest = useStore(state => state.isGolfhaftetRequest);
  const { startDate, endDate } = useDatesFilter();
  const prevStartDate = usePrevious(startDate);
  const prevEndDate = usePrevious(endDate);
  const [showMap, setShowMap] = useState(false);
  const selectedDate = useMemo(() => formatISODate(startDate), [startDate]);
  const endDateISO = useMemo(() => formatISODate(endDate), [endDate]);

  useEffect(() => {
    if (!selectedGolfCourse || !golfCourses) return;

    const sortedGolfCourses = filterAndSortCourses(
      golfCourses,
      selectedGolfCourse,
      isGolfhaftetRequest
    );

    const visibleCourses = sortPromotedGolfCourses(sortedGolfCourses)
      .filter(golfCourse => {
        if (
          golfCourse.promotion === GolfClubPromotion.Recommended ||
          golfCourse.promotion === GolfClubPromotion.Promoted
        ) {
          return isNumber(golfCourse.distance) && golfCourse.distance < MAX_DISTANCE;
        }

        return true;
      })
      .slice(0, VISIBLE_COURSES_AMOUNT_DEFAULT);

    setSortedCourses(visibleCourses);
    setMapCourses([selectedGolfCourse, ...sortedGolfCourses].map(c => ({ ...c, marked: true })));
    setShowMap(false);
  }, [golfCourses, selectedGolfCourse, isGolfhaftetRequest]);

  const golfCourseInfo = useMemo(
    () => (selectedGolfCourse ? getGolfClubInfo(selectedGolfCourse) : null),
    [selectedGolfCourse]
  );

  useLayoutEffect(() => {
    if (
      (startDate && prevStartDate && startDate !== prevStartDate) ||
      (endDate && prevEndDate && endDate !== prevEndDate)
    ) {
      const top = availabilityRef.current?.getBoundingClientRect().top;
      const header = document.getElementById(HEADER_ELEMENT_ID);

      if (!top || !header) {
        return;
      }

      const offset = calculateHidingHeaderOffset(
        header.offsetHeight,
        window.scrollY + top,
        window.scrollY,
        getCurrentOffset(header)
      );
      const scrollTop = top + window.scrollY - (header.scrollHeight + offset) + SCROLL_TOP_OFFSET;

      const timeoutId = setTimeout(() => {
        window.scrollTo({
          top: scrollTop,
          left: 0,
          behavior: 'smooth',
        });
      }, 100);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [startDate, endDate, prevStartDate, prevEndDate]);

  if (golfCoursesQuery.isFetched && !selectedGolfCourse) {
    return <Navigate to={`/${activeLanguage.code}/`} replace />;
  }

  return (
    <>
      {golfCourseInfo?.metaDescription ? (
        <Helmet
          title={golfCourseInfo?.metaTitle || selectedGolfCourse?.golfClub?.name}
          description={golfCourseInfo.metaDescription}
        />
      ) : (
        <Helmet
          title={golfCourseInfo?.metaTitle || selectedGolfCourse?.golfClub?.name}
          descriptionTranslationKey="BookingContainer.helmetDescription"
        />
      )}

      {selectedGolfCourse ? (
        <>
          <GolfClubCarousel key={selectedGolfCourse.id} golfCourse={selectedGolfCourse} />
          <Container>
            <Card style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
              <CardBody className="p-lg-4">
                <GolfClubInfo golfCourse={selectedGolfCourse} />
                <GolfClubAvailability
                  ref={availabilityRef}
                  key={selectedGolfCourse.id}
                  golfCourse={selectedGolfCourse}
                  selectedDate={selectedDate}
                  checkinDate={selectedDate}
                  checkoutDate={endDateISO}
                  onShowMap={() => setShowMap(true)}
                  mapVisible={showMap}
                />
                <Map courses={mapCourses} hoveredCourse={selectedGolfCourse.id} showMap={showMap} />
              </CardBody>
            </Card>

            {/* {sortedCourses.length > 0 && !isGolfhaftetRequest && ( */}
            {sortedCourses.length > 0 && (
              <Card className="my-3">
                <CardBody className="p-lg-4">
                  <CardTitle tag="h2" className="mb-3">
                    <strong>{translate('golfClubPage.coursesNear.title')}</strong>{' '}
                    {selectedGolfCourse.name}
                  </CardTitle>
                  <GolfCourses
                    golfCourses={sortedCourses}
                    selectedDate={selectedDate}
                    topRatedGolfCourses={
                      popularGolfCourses.find(
                        item => item.country.id === selectedGolfCourse.country.id
                      )?.topRated
                    }
                    topBookedGolfCourses={
                      popularGolfCourses.find(
                        item => item.country.id === selectedGolfCourse.country.id
                      )?.topBooked
                    }
                  />
                </CardBody>
              </Card>
            )}
          </Container>
        </>
      ) : (
        <PageSpinner />
      )}
    </>
  );
};

export default BookingPage;
